import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../api';
import { useAuth } from '../context/AuthContext';

const BRISBANE_TZ = 'Australia/Brisbane';

const cardStyle: React.CSSProperties = {
  backgroundColor: 'var(--color-surface)',
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-lg)',
  padding: 'var(--spacing-4)',
  boxShadow: 'var(--shadow-sm)',
};

const inputStyle: React.CSSProperties = {
  padding: 'var(--spacing-2) var(--spacing-3)',
  backgroundColor: 'var(--color-surface)',
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-default)',
  color: 'var(--color-text)',
};

const presetButtonStyle: React.CSSProperties = {
  padding: 'var(--spacing-2) var(--spacing-3)',
  borderRadius: '999px',
  border: '1px solid var(--color-border)',
  backgroundColor: 'var(--color-surface)',
  color: 'var(--color-text)',
  fontWeight: 600,
  fontSize: 'var(--font-size-sm)',
};

const wholeNumberFormatter = new Intl.NumberFormat('en-AU');
const currencyFormatter = new Intl.NumberFormat('en-AU', {
  style: 'currency',
  currency: 'AUD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

type RangePreset =
  | 'TODAY'
  | 'YESTERDAY'
  | 'THIS_WEEK'
  | 'LAST_WEEK'
  | 'TWO_WEEKS'
  | 'THIS_MONTH'
  | 'LAST_MONTH'
  | 'CUSTOM';

function formatDate(date: Date) {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: BRISBANE_TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(date);
}

function getToday() {
  return formatDate(new Date());
}

function addDays(dateString: string, days: number) {
  const date = new Date(`${dateString}T12:00:00`);
  date.setDate(date.getDate() + days);
  return formatDate(date);
}

function startOfWeek(dateString: string) {
  const date = new Date(`${dateString}T12:00:00`);
  const day = date.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  date.setDate(date.getDate() + diff);
  return formatDate(date);
}

function startOfMonth(dateString: string) {
  const date = new Date(`${dateString}T12:00:00`);
  date.setDate(1);
  return formatDate(date);
}

function endOfMonth(dateString: string) {
  const date = new Date(`${dateString}T12:00:00`);
  date.setMonth(date.getMonth() + 1, 0);
  return formatDate(date);
}

function rangeFromPreset(preset: RangePreset) {
  const today = getToday();
  switch (preset) {
    case 'TODAY':
      return { startDate: today, endDate: today };
    case 'YESTERDAY': {
      const yesterday = addDays(today, -1);
      return { startDate: yesterday, endDate: yesterday };
    }
    // "This week" and "this month" mean SO FAR, not the whole calendar
    // period. Running to the end of the week put days that have not happened
    // into the range: revenue only accrues to today, but amortised expenses
    // (rent and the rest) spread across every day in it, so the report showed
    // a loss that was really just unearned days.
    case 'THIS_WEEK':
      return { startDate: startOfWeek(today), endDate: today };
    case 'LAST_WEEK': {
      const currentWeekStart = startOfWeek(today);
      const lastWeekStart = addDays(currentWeekStart, -7);
      return { startDate: lastWeekStart, endDate: addDays(lastWeekStart, 6) };
    }
    case 'TWO_WEEKS':
      return { startDate: addDays(today, -13), endDate: today };
    case 'THIS_MONTH':
      return { startDate: startOfMonth(today), endDate: today };
    case 'LAST_MONTH': {
      const currentMonthStart = startOfMonth(today);
      const lastMonthDay = addDays(currentMonthStart, -1);
      return { startDate: startOfMonth(lastMonthDay), endDate: endOfMonth(lastMonthDay) };
    }
    case 'CUSTOM':
    default:
      return { startDate: startOfWeek(today), endDate: today };
  }
}

function currency(value: number) {
  return currencyFormatter.format(Number(value || 0));
}

function count(value: number) {
  return wholeNumberFormatter.format(Number(value || 0));
}

function minutes(value: number) {
  return `${wholeNumberFormatter.format(Math.round(Number(value || 0)))} min`;
}

function deltaText(delta: { value: number; pct: number | null }) {
  const formattedValue = wholeNumberFormatter.format(Math.abs(Number(delta.value || 0)));
  if (delta.pct === null) return `${delta.value >= 0 ? '+' : '-'}${formattedValue}`;
  return `${delta.value >= 0 ? '+' : '-'}${formattedValue} (${delta.pct >= 0 ? '+' : ''}${delta.pct}%)`;
}

// ─── Chart colours ────────────────────────────────────────────────────────────

const CHART_COLORS = [
  'var(--color-accent)',
  '#6c8ebf',
  '#82b366',
  '#d6932a',
  '#9673a6',
  '#ae4132',
  '#5ba3a0',
];

// ─── FloatingTooltip ─────────────────────────────────────────────────────────

type TipState = { x: number; y: number; lines: string[] } | null;

function FloatingTooltip({ tip }: { tip: TipState }) {
  if (!tip) return null;
  return (
    <div
      style={{
        position: 'fixed',
        left: tip.x + 14,
        top: tip.y - 10,
        backgroundColor: '#111',
        color: '#f5f5f5',
        padding: '6px 10px',
        borderRadius: 6,
        fontSize: 12,
        fontWeight: 600,
        pointerEvents: 'none',
        zIndex: 99999,
        boxShadow: '0 4px 16px rgba(0,0,0,0.5)',
        border: '1px solid rgba(255,255,255,0.12)',
        whiteSpace: 'nowrap',
        lineHeight: 1.6,
      }}
    >
      {tip.lines.map((l, i) => (
        <div key={i}>{l}</div>
      ))}
    </div>
  );
}

// ─── HorizontalBarChart ───────────────────────────────────────────────────────

function HorizontalBarChart({
  data,
  valueFormatter = (v: number) => String(v),
  maxHeight = 320,
}: {
  data: { label: string; value: number; subtitle?: string; color?: string }[];
  valueFormatter?: (v: number) => string;
  maxHeight?: number;
}) {
  const [tip, setTip] = useState<TipState>(null);
  const max = Math.max(...data.map((d) => d.value), 1);
  return (
    <>
      <FloatingTooltip tip={tip} />
      <div style={{ display: 'grid', gap: 'var(--spacing-3)', maxHeight, overflow: 'auto' }}>
        {data.map((item, i) => (
          <div key={i}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'baseline',
                marginBottom: 6,
              }}
            >
              <span style={{ fontSize: 'var(--font-size-sm)', fontWeight: 600 }}>
                {item.label}
                {item.subtitle && (
                  <span
                    style={{ color: 'var(--color-text-muted)', fontWeight: 400, marginLeft: 6 }}
                  >
                    {item.subtitle}
                  </span>
                )}
              </span>
              <span
                style={{
                  fontSize: 'var(--font-size-sm)',
                  fontWeight: 700,
                  marginLeft: 8,
                  flexShrink: 0,
                }}
              >
                {valueFormatter(item.value)}
              </span>
            </div>
            <div
              style={{
                height: 14,
                backgroundColor: 'var(--color-border, rgba(0,0,0,0.12))',
                borderRadius: 7,
                overflow: 'hidden',
                cursor: 'default',
              }}
              onMouseEnter={(e) =>
                setTip({
                  x: e.clientX,
                  y: e.clientY,
                  lines: [
                    item.label + (item.subtitle ? ` · ${item.subtitle}` : ''),
                    valueFormatter(item.value),
                  ],
                })
              }
              onMouseMove={(e) =>
                setTip((prev) => (prev ? { ...prev, x: e.clientX, y: e.clientY } : null))
              }
              onMouseLeave={() => setTip(null)}
            >
              <div
                style={{
                  height: '100%',
                  width: `${(item.value / max) * 100}%`,
                  backgroundColor: item.color || CHART_COLORS[i % CHART_COLORS.length],
                  borderRadius: 7,
                  transition: 'width 0.6s cubic-bezier(0.4,0,0.2,1)',
                  minWidth: item.value > 0 ? 6 : 0,
                }}
              />
            </div>
          </div>
        ))}
      </div>
    </>
  );
}
// ─── KpiCard ──────────────────────────────────────────────────────────────────

function KpiCard({
  label,
  value,
  accent = false,
  sub,
  children,
}: {
  label: string;
  value: string;
  accent?: boolean;
  sub?: string;
  children?: React.ReactNode;
}) {
  return (
    <div style={cardStyle}>
      <p
        style={{
          color: 'var(--color-text-muted)',
          fontSize: 'var(--font-size-sm)',
          marginBottom: 'var(--spacing-2)',
        }}
      >
        {label}
      </p>
      <p
        style={{
          fontSize: 'var(--font-size-3xl)',
          fontWeight: 700,
          color: accent ? 'var(--color-accent)' : 'var(--color-text)',
          lineHeight: 1.1,
        }}
      >
        {value}
      </p>
      {sub && (
        <p
          style={{
            color: 'var(--color-text-muted)',
            fontSize: 'var(--font-size-xs)',
            marginTop: 'var(--spacing-1)',
          }}
        >
          {sub}
        </p>
      )}
      {children}
    </div>
  );
}

// ─── MetricCard ───────────────────────────────────────────────────────────────

function MetricCard({
  label,
  value,
  accent = false,
  compact = false,
}: {
  label: string;
  value: string;
  accent?: boolean;
  compact?: boolean;
}) {
  return (
    <div style={{ ...cardStyle, padding: compact ? 'var(--spacing-3)' : 'var(--spacing-4)' }}>
      <p
        style={{
          color: 'var(--color-text-muted)',
          fontSize: 'var(--font-size-sm)',
          marginBottom: 'var(--spacing-2)',
        }}
      >
        {label}
      </p>
      <p
        style={{
          fontSize: compact ? 'var(--font-size-xl)' : 'var(--font-size-2xl)',
          fontWeight: 700,
          color: accent ? 'var(--color-accent)' : 'var(--color-text)',
        }}
      >
        {value}
      </p>
    </div>
  );
}

// The three lead-time buckets are cuts of the Completed Bookings figure
// directly above them, so each shows its share as well as its count — "12
// (9%)" reads better than a bare number when the periods differ in length.
function shareOfVisits(period: any, key: string) {
  const value = Number(period?.[key] || 0);
  const total = Number(period?.completed_bookings || 0);
  if (total <= 0) return count(value);
  return `${count(value)} (${Math.round((value / total) * 100)}%)`;
}

const occCell: React.CSSProperties = {
  padding: 'var(--spacing-2) var(--spacing-2)',
  fontSize: 'var(--font-size-sm)',
  whiteSpace: 'nowrap',
};

// ─── ComparisonRow ────────────────────────────────────────────────────────────

function ComparisonRow({
  label,
  current,
  previous,
  delta,
}: {
  label: string;
  current: string;
  previous: string;
  delta: string;
}) {
  const isPositive = !delta.startsWith('-');
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '1.2fr 0.8fr 0.8fr 1fr',
        gap: 'var(--spacing-2)',
        alignItems: 'center',
        padding: 'var(--spacing-2) 0',
        borderBottom: '1px solid var(--color-border)',
      }}
    >
      <strong style={{ fontSize: 'var(--font-size-sm)' }}>{label}</strong>
      <span style={{ fontSize: 'var(--font-size-sm)' }}>{current}</span>
      <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-muted)' }}>
        {previous}
      </span>
      <span
        style={{
          textAlign: 'right',
          fontSize: 'var(--font-size-sm)',
          fontWeight: 600,
          color: isPositive ? '#2f7d5b' : 'var(--color-error)',
        }}
      >
        {delta}
      </span>
    </div>
  );
}

// ─── PresetPicker ─────────────────────────────────────────────────────────────

const PRESET_LABELS: Record<RangePreset, string> = {
  TODAY: 'Today',
  YESTERDAY: 'Yesterday',
  THIS_WEEK: 'This Week',
  LAST_WEEK: 'Last Week',
  TWO_WEEKS: 'Two Weeks',
  THIS_MONTH: 'This Month',
  LAST_MONTH: 'Last Month',
  CUSTOM: 'Custom',
};

function PresetPicker({
  presets,
  activePreset,
  onPreset,
  customRange,
  onCustomRange,
}: {
  presets: RangePreset[];
  activePreset: RangePreset;
  onPreset: (p: RangePreset) => void;
  customRange: { startDate: string; endDate: string };
  onCustomRange: (r: { startDate: string; endDate: string }) => void;
}) {
  const range = activePreset === 'CUSTOM' ? customRange : rangeFromPreset(activePreset);
  const formatDate = (d: string) => {
    const dt = new Date(`${d}T12:00:00`);
    return dt.toLocaleDateString('en-AU', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      timeZone: 'Australia/Brisbane',
    });
  };
  return (
    <div style={{ display: 'grid', gap: 'var(--spacing-2)', justifyItems: 'end' }}>
      <div
        style={{
          display: 'flex',
          gap: 'var(--spacing-2)',
          flexWrap: 'wrap',
          justifyContent: 'flex-end',
        }}
      >
        {presets.map((item) => (
          <button
            key={item}
            onClick={() => onPreset(item)}
            style={{
              ...presetButtonStyle,
              backgroundColor:
                activePreset === item ? 'var(--color-accent)' : 'var(--color-surface)',
              borderColor: activePreset === item ? 'var(--color-accent)' : 'var(--color-border)',
              color: activePreset === item ? '#1e1e1e' : 'var(--color-text)',
            }}
          >
            {PRESET_LABELS[item]}
          </button>
        ))}
      </div>
      {activePreset === 'CUSTOM' ? (
        <div style={{ display: 'flex', gap: 'var(--spacing-2)', flexWrap: 'wrap' }}>
          <input
            type="date"
            value={customRange.startDate}
            onChange={(e) => onCustomRange({ ...customRange, startDate: e.target.value })}
            style={inputStyle}
          />
          <input
            type="date"
            value={customRange.endDate}
            onChange={(e) => onCustomRange({ ...customRange, endDate: e.target.value })}
            style={inputStyle}
          />
        </div>
      ) : (
        <div style={{ fontSize: '12px', color: 'var(--color-text-muted)' }}>
          {formatDate(range.startDate)} – {formatDate(range.endDate)}
        </div>
      )}
    </div>
  );
}

// ─── Barber view ─────────────────────────────────────────────────────────────

function BarberReportsView() {
  const { user } = useAuth();
  const [preset, setPreset] = useState<RangePreset>('TODAY');
  const [customRange, setCustomRange] = useState(() => rangeFromPreset('TODAY'));
  const range = preset === 'CUSTOM' ? customRange : rangeFromPreset(preset);
  const reportParams = useMemo(
    () => ({
      startDate: range.startDate,
      endDate: range.endDate,
      ...(user?.locationId ? { locationId: String(user.locationId) } : {}),
    }),
    [range.endDate, range.startDate, user?.locationId],
  );
  const { data: performance, isLoading } = useQuery({
    queryKey: ['performance-report', reportParams],
    queryFn: () => api.getPerformanceReport(reportParams),
    staleTime: 0,
    gcTime: 0,
  });
  return (
    <div style={{ display: 'grid', gap: 'var(--spacing-4)' }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: 'var(--spacing-4)',
          flexWrap: 'wrap',
        }}
      >
        <div>
          <h1 style={{ marginBottom: 'var(--spacing-2)' }}>Reports</h1>
          <p style={{ color: 'var(--color-text-muted)' }}>
            Completed services and revenue for your location.
          </p>
        </div>
        <PresetPicker
          presets={['THIS_WEEK', 'LAST_WEEK']}
          activePreset={preset}
          onPreset={setPreset}
          customRange={customRange}
          onCustomRange={setCustomRange}
        />
      </div>
      {isLoading ? (
        <p style={{ color: 'var(--color-text-muted)' }}>Loading report...</p>
      ) : (
        <>
          <section
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
              gap: 'var(--spacing-4)',
            }}
          >
            <div style={cardStyle}>
              <p
                style={{
                  color: 'var(--color-text-muted)',
                  fontSize: 'var(--font-size-sm)',
                  marginBottom: 'var(--spacing-2)',
                }}
              >
                Completed Services
              </p>
              <p style={{ fontSize: 'var(--font-size-3xl)', fontWeight: 700 }}>
                {count(performance?.totals?.completed_services || 0)}
              </p>
            </div>
            <div style={cardStyle}>
              <p
                style={{
                  color: 'var(--color-text-muted)',
                  fontSize: 'var(--font-size-sm)',
                  marginBottom: 'var(--spacing-2)',
                }}
              >
                Completed Bookings
              </p>
              <p style={{ fontSize: 'var(--font-size-3xl)', fontWeight: 700 }}>
                {count(performance?.totals?.completed_bookings || 0)}
              </p>
            </div>
            <div style={cardStyle}>
              <p
                style={{
                  color: 'var(--color-text-muted)',
                  fontSize: 'var(--font-size-sm)',
                  marginBottom: 'var(--spacing-2)',
                }}
              >
                Completed Revenue
              </p>
              <p
                style={{
                  fontSize: 'var(--font-size-3xl)',
                  fontWeight: 700,
                  color: 'var(--color-accent)',
                }}
              >
                {currency(performance?.totals?.completed_revenue || 0)}
              </p>
            </div>
          </section>
          <section
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
              gap: 'var(--spacing-4)',
            }}
          >
            <div style={cardStyle}>
              <h2 style={{ marginBottom: 'var(--spacing-3)' }}>By Location</h2>
              {!performance?.locations?.length ? (
                <p style={{ color: 'var(--color-text-muted)' }}>
                  No completed work in this date range.
                </p>
              ) : (
                <div style={{ display: 'grid', gap: 'var(--spacing-2)' }}>
                  {performance.locations.map((location: any) => (
                    <div
                      key={location.location_id}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        gap: 'var(--spacing-3)',
                        padding: 'var(--spacing-3)',
                        backgroundColor: 'var(--color-surface-elevated)',
                        border: '1px solid var(--color-border)',
                        borderRadius: 'var(--radius-default)',
                      }}
                    >
                      <div>
                        <strong>{location.location_name}</strong>
                        <p
                          style={{
                            color: 'var(--color-text-muted)',
                            marginTop: 'var(--spacing-1)',
                          }}
                        >
                          {location.completed_services} services · {location.completed_bookings}{' '}
                          bookings
                        </p>
                      </div>
                      <strong>{currency(location.completed_revenue)}</strong>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div style={cardStyle}>
              <h2 style={{ marginBottom: 'var(--spacing-3)' }}>By Barber</h2>
              {!performance?.barbers?.length ? (
                <p style={{ color: 'var(--color-text-muted)' }}>
                  No completed work in this date range.
                </p>
              ) : (
                <div style={{ display: 'grid', gap: 'var(--spacing-2)' }}>
                  {performance.barbers.map((barber: any) => (
                    <div
                      key={barber.barber_id}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        gap: 'var(--spacing-3)',
                        padding: 'var(--spacing-3)',
                        backgroundColor: 'var(--color-surface-elevated)',
                        border: '1px solid var(--color-border)',
                        borderRadius: 'var(--radius-default)',
                      }}
                    >
                      <div>
                        <strong>{barber.barber_name}</strong>
                        <p
                          style={{
                            color: 'var(--color-text-muted)',
                            marginTop: 'var(--spacing-1)',
                          }}
                        >
                          {barber.location_name} · {barber.completed_services} services
                        </p>
                      </div>
                      <strong>{currency(barber.completed_revenue)}</strong>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </section>
        </>
      )}
    </div>
  );
}

// ─── Admin tab types ──────────────────────────────────────────────────────────

type AdminTab = 'overview' | 'payroll' | 'utilization' | 'comparison' | 'recent-bookings';

const ADMIN_TABS: { id: AdminTab; label: string }[] = [
  { id: 'overview', label: 'Overview' },
  { id: 'recent-bookings', label: 'Recent Bookings' },
  { id: 'payroll', label: 'Payroll' },
  { id: 'utilization', label: 'Utilization' },
  { id: 'comparison', label: 'Comparison' },
];

type RecentFilter = '1h' | '6h' | '1d';
const RECENT_FILTERS: { id: RecentFilter; label: string; hours: number }[] = [
  { id: '1h', label: 'Last Hour', hours: 1 },
  { id: '6h', label: 'Last 6 Hours', hours: 6 },
  { id: '1d', label: 'Last 24 Hours', hours: 24 },
];

// ─── Admin view ───────────────────────────────────────────────────────────────

function AdminReportsView() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<AdminTab>('overview');
  const [preset, setPreset] = useState<RangePreset>('TODAY');
  const [customRange, setCustomRange] = useState(() => rangeFromPreset('TODAY'));
  const [expandedPayrollBarber, setExpandedPayrollBarber] = useState<number | null>(null);
  const range = preset === 'CUSTOM' ? customRange : rangeFromPreset(preset);
  const reportParams = useMemo(
    () => ({
      startDate: range.startDate,
      endDate: range.endDate,
      ...(user?.locationId ? { locationId: String(user.locationId) } : {}),
    }),
    [range.endDate, range.startDate, user?.locationId],
  );

  const { data: performance, isLoading: loadingPerformance } = useQuery({
    queryKey: ['performance-report', reportParams],
    queryFn: () => api.getPerformanceReport(reportParams),
    staleTime: 0,
    gcTime: 0,
  });
  const { data: financial } = useQuery({
    queryKey: ['financial-report', reportParams],
    queryFn: () => api.getFinancialReport(reportParams),
    staleTime: 0,
    gcTime: 0,
  });
  const { data: utilization, isLoading: loadingUtilization } = useQuery({
    queryKey: ['utilization-report', reportParams],
    queryFn: () => api.getUtilizationReport(reportParams),
    staleTime: 0,
    gcTime: 0,
  });
  const { data: comparison, isLoading: loadingComparison } = useQuery({
    queryKey: ['comparison-report', reportParams],
    queryFn: () => api.getComparisonReport(reportParams),
    staleTime: 0,
    gcTime: 0,
  });

  // Recent bookings state
  const [recentFilter, setRecentFilter] = useState<RecentFilter>('1d');
  const recentCreatedAfter = useMemo(() => {
    const hours = RECENT_FILTERS.find((f) => f.id === recentFilter)?.hours ?? 24;
    return new Date(Date.now() - hours * 3600000).toISOString();
  }, [recentFilter]);
  const { data: recentBookings, isLoading: loadingRecent } = useQuery({
    queryKey: ['recent-bookings', recentCreatedAfter],
    queryFn: () => api.getBookings({ createdAfter: recentCreatedAfter }),
    enabled: activeTab === 'recent-bookings',
    refetchInterval: 30000,
  });
  const { data: allLocations } = useQuery({
    queryKey: ['locations'],
    queryFn: () => api.getLocations(),
    enabled: activeTab === 'recent-bookings',
    staleTime: 300000,
  });
  const { data: allBarbers } = useQuery({
    queryKey: ['barbers'],
    queryFn: () => api.getBarbers(),
    enabled: activeTab === 'recent-bookings',
    staleTime: 300000,
  });
  const locationMap = useMemo(() => {
    const m = new Map<number, string>();
    (allLocations || []).forEach((l: any) => m.set(l.id, l.name));
    return m;
  }, [allLocations]);
  const barberMap = useMemo(() => {
    const m = new Map<number, string>();
    (allBarbers || []).forEach((b: any) => m.set(b.id, b.name));
    return m;
  }, [allBarbers]);

  // Payroll
  const { data: payroll, isLoading: loadingPayroll } = useQuery({
    queryKey: ['payroll-report', reportParams],
    queryFn: () => api.getPayrollReport(reportParams),
    enabled: activeTab === 'payroll',
    staleTime: 0,
    gcTime: 0,
  });

  // Chart data
  const barberChartData = (performance?.barbers || []).map((b: any) => ({
    label: b.barber_name,
    value: Number(b.completed_revenue || 0),
    subtitle: b.location_name,
  }));
  const locationChartData = (performance?.locations || []).map((l: any) => ({
    label: l.location_name,
    value: Number(l.completed_revenue || 0),
  }));
  const utilizationChartData = (utilization?.barbers || [])
    .slice()
    .sort((a: any, b: any) => Number(b.utilization_pct) - Number(a.utilization_pct))
    .map((b: any) => ({
      label: b.barber_name,
      value: Number(b.utilization_pct || 0),
      subtitle: b.location_name,
      color:
        Number(b.utilization_pct) > 70
          ? '#2f7d5b'
          : Number(b.utilization_pct) > 40
            ? 'var(--color-accent)'
            : '#ae4132',
    }));

  return (
    <div style={{ display: 'grid', gap: 'var(--spacing-4)' }}>
      {/* Header */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: 'var(--spacing-4)',
          flexWrap: 'wrap',
        }}
      >
        <div>
          <h1 style={{ marginBottom: 'var(--spacing-2)' }}>Reports</h1>
          <p style={{ color: 'var(--color-text-muted)' }}>
            Performance, financials, utilization, and insights across all locations.
          </p>
        </div>
        {activeTab !== 'recent-bookings' && activeTab !== 'payroll' && (
          <PresetPicker
            presets={[
              'TODAY',
              'YESTERDAY',
              'THIS_WEEK',
              'LAST_WEEK',
              'TWO_WEEKS',
              'THIS_MONTH',
              'LAST_MONTH',
              'CUSTOM',
            ]}
            activePreset={preset}
            onPreset={setPreset}
            customRange={customRange}
            onCustomRange={setCustomRange}
          />
        )}
      </div>

      {/* Tabs */}
      <div
        style={{
          display: 'flex',
          gap: 0,
          borderBottom: '2px solid var(--color-border)',
          overflowX: 'auto',
        }}
      >
        {ADMIN_TABS.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={{
              padding: 'var(--spacing-2) var(--spacing-4)',
              background: 'none',
              border: 'none',
              borderBottom:
                activeTab === tab.id ? '2px solid var(--color-accent)' : '2px solid transparent',
              marginBottom: -2,
              color: activeTab === tab.id ? 'var(--color-accent)' : 'var(--color-text-muted)',
              fontWeight: activeTab === tab.id ? 700 : 400,
              fontSize: 'var(--font-size-sm)',
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              transition: 'color 0.2s, border-color 0.2s',
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* ── Overview ─────────────────────────────────────────────────── */}
      {activeTab === 'overview' &&
        (loadingPerformance ? (
          <p style={{ color: 'var(--color-text-muted)' }}>Loading...</p>
        ) : (
          <>
            <section
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))',
                gap: 'var(--spacing-4)',
              }}
            >
              <div style={{ ...cardStyle, gridColumn: 'span 2' }}>
                <p
                  style={{
                    color: 'var(--color-text-muted)',
                    fontSize: 'var(--font-size-sm)',
                    marginBottom: 'var(--spacing-2)',
                  }}
                >
                  Profit
                </p>
                <p
                  style={{
                    fontSize: 'var(--font-size-3xl)',
                    fontWeight: 700,
                    color: 'var(--color-accent)',
                    lineHeight: 1.1,
                  }}
                >
                  {currency(financial?.summary?.net_profit || 0)}
                </p>
                <div
                  style={{
                    marginTop: 'var(--spacing-2)',
                    fontSize: 'var(--font-size-xs)',
                    color: 'var(--color-text-muted)',
                    lineHeight: 1.8,
                  }}
                >
                  <div>GST (10%): {currency(financial?.summary?.gst_total || 0)}</div>
                  <div>
                    Wages:{' '}
                    {currency(
                      (financial?.summary?.wages_total || 0) +
                        (financial?.summary?.super_total || 0),
                    )}
                    {(() => {
                      const wbt = financial?.summary?.wages_by_type;
                      if (!wbt) return null;
                      const parts: string[] = [];
                      if (wbt.FULL_TIME) parts.push(`${wbt.FULL_TIME.count} FT`);
                      if (wbt.PART_TIME) parts.push(`${wbt.PART_TIME.count} PT`);
                      if (wbt.CASUAL) parts.push(`${wbt.CASUAL.count} Casual`);
                      return parts.length ? ` (${parts.join(', ')})` : null;
                    })()}
                  </div>
                  <div>
                    Expenses: {currency(financial?.summary?.expenses_total || 0)}
                    {financial?.summary?.working_days
                      ? ` (${financial.summary.working_days} days)`
                      : ''}
                  </div>
                  {(financial?.summary?.bonus_total || 0) > 0 && (
                    <div>Bonus: {currency(financial.summary.bonus_total)}</div>
                  )}
                </div>
              </div>
              <KpiCard
                label="Total Revenue"
                value={currency(performance?.totals?.completed_revenue || 0)}
                accent
                sub={`Bookings: ${count(performance?.totals?.completed_bookings || 0)}`}
              />
              <KpiCard
                label="Services"
                value={currency(performance?.totals?.service_revenue || 0)}
                sub={`Completed: ${count(performance?.totals?.completed_services || 0)}`}
              >
                <div
                  style={{
                    fontSize: 'var(--font-size-xs)',
                    color: 'var(--color-text-muted)',
                    marginTop: 'var(--spacing-1)',
                  }}
                >
                  <div>Cancelled: {count(performance?.totals?.cancelled_bookings || 0)}</div>
                  <div>No-show: {count(performance?.totals?.no_show_bookings || 0)}</div>
                </div>
              </KpiCard>
              <KpiCard label="Products" value={currency(performance?.totals?.product_revenue || 0)}>
                {performance?.top_products?.length > 0 && (
                  <div
                    style={{
                      fontSize: 'var(--font-size-xs)',
                      color: 'var(--color-text-muted)',
                      marginTop: 'var(--spacing-2)',
                      lineHeight: 1.6,
                    }}
                  >
                    {performance.top_products.map((p: any, i: number) => (
                      <div key={i}>
                        {p.name} ×{p.qty} · {currency(p.total)}
                      </div>
                    ))}
                  </div>
                )}
              </KpiCard>
              {/* Loyalty card / Voucher Discount — revenue above is already net of
                  these. Free loyalty services are counted per barber because
                  the stamp cards are paper. */}
              <KpiCard
                label="Discounts"
                value={currency(performance?.discounts?.total || 0)}
                sub={`Loyalty: ${count(performance?.discounts?.loyalty_count || 0)} free · Voucher: ${count(performance?.discounts?.voucher_count || 0)}`}
              >
                {performance?.discounts?.loyalty_by_barber?.length > 0 && (
                  <div
                    style={{
                      fontSize: 'var(--font-size-xs)',
                      color: 'var(--color-text-muted)',
                      marginTop: 'var(--spacing-2)',
                      lineHeight: 1.6,
                    }}
                  >
                    {performance.discounts.loyalty_by_barber.map((b: any) => (
                      <div key={b.barber_name}>
                        🎟 {b.barber_name} ×{b.count}
                      </div>
                    ))}
                  </div>
                )}
              </KpiCard>
              {/* Rebook at the till — of the paid visits where the next cut
                  was offered, how many left with a booking. */}
              <KpiCard
                label="Rebooked at Till"
                value={performance?.rebook?.rate == null ? '—' : `${performance.rebook.rate}%`}
                sub={`${count(performance?.rebook?.rebooked || 0)} of ${count(
                  (performance?.rebook?.rebooked || 0) + (performance?.rebook?.skipped || 0),
                )} offered`}
              >
                {performance?.rebook?.by_barber?.length > 0 && (
                  <div
                    style={{
                      fontSize: 'var(--font-size-xs)',
                      color: 'var(--color-text-muted)',
                      marginTop: 'var(--spacing-2)',
                      lineHeight: 1.6,
                    }}
                  >
                    {performance.rebook.by_barber.map((b: any) => (
                      <div key={b.barber_name}>
                        {b.barber_name} · {b.rate}% ({b.rebooked} of {b.rebooked + b.skipped})
                      </div>
                    ))}
                  </div>
                )}
              </KpiCard>
              <KpiCard
                label="Cash Received"
                value={currency(performance?.totals?.cash_received || 0)}
              />
              <KpiCard
                label="Card Received"
                value={currency(performance?.totals?.card_received || 0)}
              />
              <KpiCard
                label="Forecast"
                value={currency(performance?.totals?.forecast_revenue || 0)}
                sub={`Bookings: ${count(performance?.totals?.forecast_bookings || 0)}`}
              />
            </section>
            <section
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                gap: 'var(--spacing-4)',
              }}
            >
              <div style={cardStyle}>
                <h2 style={{ marginBottom: 'var(--spacing-4)' }}>Revenue by Location</h2>
                {!locationChartData.length ? (
                  <p style={{ color: 'var(--color-text-muted)' }}>No data.</p>
                ) : (
                  <HorizontalBarChart data={locationChartData} valueFormatter={currency} />
                )}
                {/* Cash takings per location for the selected date range —
                    a quick reconciliation reference so staff can match the
                    till against the report before banking. */}
                {performance?.locations?.length ? (
                  <div
                    style={{
                      marginTop: 'var(--spacing-4)',
                      paddingTop: 'var(--spacing-3)',
                      borderTop: '1px solid var(--color-border)',
                    }}
                  >
                    <div
                      style={{
                        fontSize: '13px',
                        fontWeight: 600,
                        color: 'var(--color-text-muted)',
                        marginBottom: 'var(--spacing-2)',
                      }}
                    >
                      Cash available
                    </div>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                      <tbody>
                        {performance.locations.map((loc: any) => (
                          <tr
                            key={loc.location_id}
                            style={{ borderTop: '1px solid var(--color-border)' }}
                          >
                            <td style={{ padding: '6px 0' }}>{loc.location_name}</td>
                            <td
                              style={{
                                padding: '6px 0',
                                textAlign: 'right',
                                fontWeight: 600,
                                fontVariantNumeric: 'tabular-nums',
                              }}
                            >
                              {currency(loc.cash_received || 0)}
                            </td>
                          </tr>
                        ))}
                        <tr
                          style={{
                            borderTop: '1px solid var(--color-border)',
                            color: 'var(--color-text-muted)',
                          }}
                        >
                          <td style={{ padding: '6px 0' }}>Total</td>
                          <td
                            style={{
                              padding: '6px 0',
                              textAlign: 'right',
                              fontWeight: 600,
                              fontVariantNumeric: 'tabular-nums',
                            }}
                          >
                            {currency(
                              performance.locations.reduce(
                                (s: number, l: any) => s + Number(l.cash_received || 0),
                                0,
                              ),
                            )}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                ) : null}
              </div>
              <div style={cardStyle}>
                <h2 style={{ marginBottom: 'var(--spacing-4)' }}>Revenue by Barber</h2>
                {!barberChartData.length ? (
                  <p style={{ color: 'var(--color-text-muted)' }}>No data.</p>
                ) : (
                  <HorizontalBarChart data={barberChartData} valueFormatter={currency} />
                )}
              </div>
            </section>

            {financial?.by_location?.length ? (
              <section style={{ ...cardStyle, marginTop: 'var(--spacing-4)' }}>
                <h2 style={{ marginBottom: 'var(--spacing-4)' }}>Profit by Location</h2>
                <div style={{ overflowX: 'auto' }}>
                  <table
                    style={{
                      width: '100%',
                      minWidth: 640,
                      borderCollapse: 'collapse',
                      fontSize: '14px',
                    }}
                  >
                    <thead>
                      <tr style={{ color: 'var(--color-text-muted)', textAlign: 'right' }}>
                        <th style={{ padding: '8px 12px', textAlign: 'left' }}>Location</th>
                        <th style={{ padding: '8px 12px' }}>Sales</th>
                        <th style={{ padding: '8px 12px' }}>GST</th>
                        <th style={{ padding: '8px 12px' }}>Wages + Super</th>
                        <th style={{ padding: '8px 12px' }}>Bonus</th>
                        <th style={{ padding: '8px 12px' }}>Expenses</th>
                        <th style={{ padding: '8px 12px' }}>Profit</th>
                      </tr>
                    </thead>
                    <tbody>
                      {financial.by_location.map((loc: any) => (
                        <tr
                          key={loc.location_id}
                          style={{
                            borderTop: '1px solid var(--color-border)',
                            fontVariantNumeric: 'tabular-nums',
                          }}
                        >
                          <td style={{ padding: '10px 12px', fontWeight: 600 }}>
                            {loc.location_name}
                          </td>
                          <td style={{ padding: '10px 12px', textAlign: 'right' }}>
                            {currency(loc.sales_total)}
                          </td>
                          <td
                            style={{
                              padding: '10px 12px',
                              textAlign: 'right',
                              color: 'var(--color-text-muted)',
                            }}
                          >
                            ({currency(loc.gst_total)})
                          </td>
                          <td
                            style={{
                              padding: '10px 12px',
                              textAlign: 'right',
                              color: 'var(--color-text-muted)',
                            }}
                          >
                            ({currency(loc.wages_total + loc.super_total)})
                          </td>
                          <td
                            style={{
                              padding: '10px 12px',
                              textAlign: 'right',
                              color: 'var(--color-text-muted)',
                            }}
                          >
                            ({currency(loc.bonus_total)})
                          </td>
                          <td
                            style={{
                              padding: '10px 12px',
                              textAlign: 'right',
                              color: 'var(--color-text-muted)',
                            }}
                          >
                            ({currency(loc.expenses_total)})
                          </td>
                          <td
                            style={{
                              padding: '10px 12px',
                              textAlign: 'right',
                              fontWeight: 700,
                              color:
                                loc.net_profit >= 0
                                  ? 'var(--color-accent)'
                                  : 'var(--color-error, #dc3545)',
                            }}
                          >
                            {currency(loc.net_profit)}
                          </td>
                        </tr>
                      ))}
                      <tr
                        style={{
                          borderTop: '2px solid var(--color-border)',
                          fontVariantNumeric: 'tabular-nums',
                          color: 'var(--color-text-muted)',
                        }}
                      >
                        <td style={{ padding: '10px 12px', fontWeight: 600 }}>Total</td>
                        <td style={{ padding: '10px 12px', textAlign: 'right' }}>
                          {currency(
                            financial.by_location.reduce(
                              (s: number, l: any) => s + Number(l.sales_total || 0),
                              0,
                            ),
                          )}
                        </td>
                        <td style={{ padding: '10px 12px', textAlign: 'right' }}>
                          (
                          {currency(
                            financial.by_location.reduce(
                              (s: number, l: any) => s + Number(l.gst_total || 0),
                              0,
                            ),
                          )}
                          )
                        </td>
                        <td style={{ padding: '10px 12px', textAlign: 'right' }}>
                          (
                          {currency(
                            financial.by_location.reduce(
                              (s: number, l: any) =>
                                s + Number(l.wages_total || 0) + Number(l.super_total || 0),
                              0,
                            ),
                          )}
                          )
                        </td>
                        <td style={{ padding: '10px 12px', textAlign: 'right' }}>
                          (
                          {currency(
                            financial.by_location.reduce(
                              (s: number, l: any) => s + Number(l.bonus_total || 0),
                              0,
                            ),
                          )}
                          )
                        </td>
                        <td style={{ padding: '10px 12px', textAlign: 'right' }}>
                          (
                          {currency(
                            financial.by_location.reduce(
                              (s: number, l: any) => s + Number(l.expenses_total || 0),
                              0,
                            ),
                          )}
                          )
                        </td>
                        <td
                          style={{
                            padding: '10px 12px',
                            textAlign: 'right',
                            fontWeight: 700,
                            color: 'var(--color-text)',
                          }}
                        >
                          {currency(
                            financial.by_location.reduce(
                              (s: number, l: any) => s + Number(l.net_profit || 0),
                              0,
                            ),
                          )}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </section>
            ) : null}
          </>
        ))}

      {/* ── Utilization ──────────────────────────────────────────────── */}
      {activeTab === 'utilization' && (
        <section style={{ display: 'grid', gap: 'var(--spacing-4)' }}>
          <h2>Chair Occupancy</h2>
          {loadingUtilization ? (
            <p style={{ color: 'var(--color-text-muted)' }}>Loading utilization...</p>
          ) : (
            <>
              <p style={{ color: 'var(--color-text-muted)', marginTop: 0 }}>
                How the rostered hours were used, after breaks.
              </p>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))',
                  gap: 'var(--spacing-4)',
                }}
              >
                <MetricCard
                  label="Available"
                  value={minutes(utilization?.occupancy?.totals?.available_minutes || 0)}
                />
                <MetricCard
                  label="Sold"
                  value={minutes(utilization?.occupancy?.totals?.sold_minutes || 0)}
                />
                <MetricCard
                  label="Cancelled + No-show"
                  value={minutes(
                    (utilization?.occupancy?.totals?.cancelled_minutes || 0) +
                      (utilization?.occupancy?.totals?.no_show_minutes || 0),
                  )}
                />
                <MetricCard
                  label="Never booked"
                  value={minutes(utilization?.occupancy?.totals?.unsold_minutes || 0)}
                />
                <MetricCard
                  label="Occupancy"
                  value={`${utilization?.occupancy?.totals?.occupancy_pct || 0}%`}
                  accent
                />
              </div>

              <div style={cardStyle}>
                <h3 style={{ marginTop: 0 }}>Per barber</h3>
                <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 560 }}>
                    <thead>
                      <tr style={{ textAlign: 'left', color: 'var(--color-text-muted)' }}>
                        <th style={occCell}>BARBER</th>
                        <th style={occCell}>AVAILABLE</th>
                        <th style={occCell}>SOLD</th>
                        <th style={occCell}>LOST</th>
                        <th style={occCell}>NEVER BOOKED</th>
                        <th style={{ ...occCell, textAlign: 'right' }}>OCCUPANCY</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(utilization?.occupancy?.barbers || []).map((b: any) => (
                        <tr
                          key={b.barber_id}
                          style={{ borderTop: '1px solid var(--color-border)' }}
                        >
                          <td style={{ ...occCell, fontWeight: 600 }}>{b.barber_name}</td>
                          <td style={occCell}>{minutes(b.available_minutes)}</td>
                          <td style={occCell}>{minutes(b.sold_minutes)}</td>
                          <td style={occCell}>
                            {minutes(b.cancelled_minutes + b.no_show_minutes)}
                            {b.cancelled_bookings + b.no_show_bookings > 0 ? (
                              <span
                                style={{ color: 'var(--color-text-muted)', fontSize: '12px' }}
                              >{` (${b.cancelled_bookings} canc, ${b.no_show_bookings} n/s)`}</span>
                            ) : null}
                          </td>
                          <td style={occCell}>{minutes(b.unsold_minutes)}</td>
                          <td style={{ ...occCell, textAlign: 'right', fontWeight: 600 }}>
                            {b.occupancy_pct}%
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <h3 style={{ marginBottom: 0 }}>Appointment timing</h3>
              <p style={{ color: 'var(--color-text-muted)', marginTop: 4 }}>
                How long appointments actually took against how long they were booked for — a check
                on whether your service durations are set right, not a measure of how busy the shop
                was. Completed appointments only.
              </p>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                  gap: 'var(--spacing-4)',
                }}
              >
                <MetricCard
                  label="Booked"
                  value={minutes(utilization?.totals?.scheduled_minutes || 0)}
                />
                <MetricCard
                  label="Actual"
                  value={minutes(utilization?.totals?.actual_minutes || 0)}
                />
                <MetricCard
                  label="Average accuracy"
                  value={`${utilization?.totals?.average_utilization_pct || 0}%`}
                />
              </div>
              <section
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                  gap: 'var(--spacing-4)',
                }}
              >
                <div style={cardStyle}>
                  <h3 style={{ marginBottom: 'var(--spacing-4)' }}>Utilization Chart</h3>
                  {!utilizationChartData.length ? (
                    <p style={{ color: 'var(--color-text-muted)' }}>No data.</p>
                  ) : (
                    <HorizontalBarChart
                      data={utilizationChartData}
                      valueFormatter={(v) => `${v}%`}
                    />
                  )}
                </div>
                <div style={cardStyle}>
                  <h3 style={{ marginBottom: 'var(--spacing-3)' }}>Detail</h3>
                  <div style={{ display: 'grid', gap: 'var(--spacing-2)' }}>
                    {(utilization?.barbers || []).map((barber: any) => (
                      <div
                        key={barber.barber_id}
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          gap: 'var(--spacing-3)',
                          padding: 'var(--spacing-3)',
                          backgroundColor: 'var(--color-surface-elevated)',
                          border: '1px solid var(--color-border)',
                          borderRadius: 'var(--radius-default)',
                        }}
                      >
                        <div>
                          <strong>{barber.barber_name}</strong>
                          <p
                            style={{
                              color: 'var(--color-text-muted)',
                              marginTop: 'var(--spacing-1)',
                            }}
                          >
                            {barber.location_name} · {barber.completed_bookings} bookings
                          </p>
                          <p
                            style={{
                              color: 'var(--color-text-muted)',
                              marginTop: 2,
                              fontSize: 'var(--font-size-xs)',
                            }}
                          >
                            Early finish {minutes(barber.early_finish_minutes)} · Overtime{' '}
                            {minutes(barber.overtime_minutes)}
                          </p>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                          <strong style={{ fontSize: 'var(--font-size-xl)' }}>
                            {barber.utilization_pct}%
                          </strong>
                          <p
                            style={{
                              color: 'var(--color-text-muted)',
                              marginTop: 'var(--spacing-1)',
                              fontSize: 'var(--font-size-xs)',
                            }}
                          >
                            {minutes(barber.actual_minutes)} / {minutes(barber.scheduled_minutes)}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </section>
            </>
          )}
        </section>
      )}

      {/* ── Comparison ───────────────────────────────────────────────── */}
      {activeTab === 'comparison' && (
        <section style={{ display: 'grid', gap: 'var(--spacing-4)' }}>
          <h2>Period Comparison</h2>
          {loadingComparison ? (
            <p style={{ color: 'var(--color-text-muted)' }}>Loading comparison...</p>
          ) : !comparison ? (
            <p style={{ color: 'var(--color-text-muted)' }}>No comparison data.</p>
          ) : (
            <>
              <p style={{ color: 'var(--color-text-muted)' }}>
                <strong>
                  {comparison.current.start_date} to {comparison.current.end_date}
                </strong>{' '}
                vs {comparison.previous.start_date} to {comparison.previous.end_date}
                {comparison.period_to_date && comparison.as_at
                  ? ` — both cut at ${comparison.as_at} so far`
                  : ''}
              </p>
              {comparison.upcoming_bookings > 0 && (
                <div
                  style={{
                    ...cardStyle,
                    padding: 'var(--spacing-3) var(--spacing-4)',
                    backgroundColor: 'var(--color-surface-elevated)',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: 'var(--spacing-2)',
                  }}
                >
                  <strong>Still in the diary</strong>
                  <span style={{ color: 'var(--color-text-muted)' }}>
                    {count(comparison.upcoming_bookings)} appointment
                    {comparison.upcoming_bookings === 1 ? '' : 's'} booked but not yet completed ·{' '}
                    {currency(comparison.upcoming_value || 0)}
                  </span>
                </div>
              )}
              <div style={cardStyle}>
                <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                  <div style={{ display: 'grid', gap: 2, minWidth: 400 }}>
                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns: '1.2fr 0.8fr 0.8fr 1fr',
                        gap: 'var(--spacing-2)',
                        alignItems: 'center',
                        padding: 'var(--spacing-2) 0',
                        marginBottom: 'var(--spacing-1)',
                      }}
                    >
                      <span
                        style={{
                          fontSize: 'var(--font-size-xs)',
                          color: 'var(--color-text-muted)',
                          fontWeight: 600,
                        }}
                      >
                        METRIC
                      </span>
                      <span
                        style={{
                          fontSize: 'var(--font-size-xs)',
                          color: 'var(--color-text-muted)',
                          fontWeight: 600,
                        }}
                      >
                        CURRENT
                      </span>
                      <span
                        style={{
                          fontSize: 'var(--font-size-xs)',
                          color: 'var(--color-text-muted)',
                          fontWeight: 600,
                        }}
                      >
                        PREVIOUS
                      </span>
                      <span
                        style={{
                          textAlign: 'right',
                          fontSize: 'var(--font-size-xs)',
                          color: 'var(--color-text-muted)',
                          fontWeight: 600,
                        }}
                      >
                        CHANGE
                      </span>
                    </div>
                    <ComparisonRow
                      label="Sales"
                      current={currency(comparison.current.sales_total || 0)}
                      previous={currency(comparison.previous.sales_total || 0)}
                      delta={deltaText(comparison.deltas?.sales_total || { value: 0, pct: null })}
                    />
                    <ComparisonRow
                      label="Services"
                      current={count(comparison.current.service_count || 0)}
                      previous={count(comparison.previous.service_count || 0)}
                      delta={deltaText(comparison.deltas?.service_count || { value: 0, pct: null })}
                    />
                    <ComparisonRow
                      label="Completed Bookings"
                      current={count(comparison.current.completed_bookings || 0)}
                      previous={count(comparison.previous.completed_bookings || 0)}
                      delta={deltaText(
                        comparison.deltas?.completed_bookings || { value: 0, pct: null },
                      )}
                    />
                    <ComparisonRow
                      label="— Walk-ins"
                      current={shareOfVisits(comparison.current, 'walk_in_bookings')}
                      previous={shareOfVisits(comparison.previous, 'walk_in_bookings')}
                      delta={deltaText(
                        comparison.deltas?.walk_in_bookings || { value: 0, pct: null },
                      )}
                    />
                    <ComparisonRow
                      label="— Booked under 24h ahead"
                      current={shareOfVisits(comparison.current, 'lead_under_24h_bookings')}
                      previous={shareOfVisits(comparison.previous, 'lead_under_24h_bookings')}
                      delta={deltaText(
                        comparison.deltas?.lead_under_24h_bookings || { value: 0, pct: null },
                      )}
                    />
                    <ComparisonRow
                      label="— Booked 24h+ ahead"
                      current={shareOfVisits(comparison.current, 'lead_over_24h_bookings')}
                      previous={shareOfVisits(comparison.previous, 'lead_over_24h_bookings')}
                      delta={deltaText(
                        comparison.deltas?.lead_over_24h_bookings || { value: 0, pct: null },
                      )}
                    />
                    <ComparisonRow
                      label="Barbers Working"
                      current={count(comparison.current.active_barbers || 0)}
                      previous={count(comparison.previous.active_barbers || 0)}
                      delta={deltaText(
                        comparison.deltas?.active_barbers || { value: 0, pct: null },
                      )}
                    />
                  </div>
                </div>
                {/* end overflowX scroll wrapper */}
              </div>
              <div
                style={{
                  ...cardStyle,
                  padding: 'var(--spacing-3) var(--spacing-4)',
                  backgroundColor: 'var(--color-surface-elevated)',
                }}
              >
                <h3 style={{ marginBottom: 'var(--spacing-2)' }}>Auto Sync Windows</h3>
                <p style={{ color: 'var(--color-text-muted)' }}>
                  Mon–Fri after 6:00 pm · Sat Mitchelton after 2:00 pm · Sat Nundah after 4:00 pm
                </p>
              </div>
            </>
          )}
        </section>
      )}

      {/* ── Payroll / Timesheet ──────────────────────────────────────── */}
      {activeTab === 'payroll' && (
        <section style={{ display: 'grid', gap: 'var(--spacing-4)' }}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 'var(--spacing-3)',
            }}
          >
            <h2 style={{ marginBottom: 0 }}>Timesheet</h2>
            <PresetPicker
              presets={[
                'TODAY',
                'YESTERDAY',
                'THIS_WEEK',
                'LAST_WEEK',
                'TWO_WEEKS',
                'THIS_MONTH',
                'LAST_MONTH',
                'CUSTOM',
              ]}
              activePreset={preset}
              onPreset={setPreset}
              customRange={customRange}
              onCustomRange={setCustomRange}
            />
          </div>
          {loadingPayroll ? (
            <p style={{ color: 'var(--color-text-muted)' }}>Loading...</p>
          ) : !payroll?.barbers?.length ? (
            <div style={cardStyle}>
              <p style={{ color: 'var(--color-text-muted)' }}>
                No shift data found for this period.
              </p>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table
                style={{
                  width: '100%',
                  borderCollapse: 'collapse',
                  fontSize: 'var(--font-size-sm)',
                }}
              >
                <thead>
                  <tr
                    style={{
                      borderBottom: '2px solid var(--color-border)',
                      textAlign: 'left',
                    }}
                  >
                    <th style={{ padding: 'var(--spacing-2) var(--spacing-3)' }}>Barber</th>
                    <th style={{ padding: 'var(--spacing-2) var(--spacing-3)' }}>Location</th>
                    <th
                      style={{ padding: 'var(--spacing-2) var(--spacing-3)', textAlign: 'right' }}
                    >
                      Ordinary Hours
                    </th>
                    <th
                      style={{ padding: 'var(--spacing-2) var(--spacing-3)', textAlign: 'right' }}
                    >
                      Saturday Penalty
                    </th>
                    <th
                      style={{ padding: 'var(--spacing-2) var(--spacing-3)', textAlign: 'right' }}
                    >
                      Total Hours
                    </th>
                    <th
                      style={{ padding: 'var(--spacing-2) var(--spacing-3)', textAlign: 'right' }}
                    >
                      Services Total
                    </th>
                    <th
                      style={{ padding: 'var(--spacing-2) var(--spacing-3)', textAlign: 'right' }}
                    >
                      Tips
                    </th>
                    <th
                      style={{ padding: 'var(--spacing-2) var(--spacing-3)', textAlign: 'right' }}
                    >
                      Bonus
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {payroll.barbers.flatMap((b: any) => {
                    const isExpanded = expandedPayrollBarber === b.barber_id;
                    return [
                      <tr
                        key={b.barber_id}
                        style={{
                          borderBottom: isExpanded ? 'none' : '1px solid var(--color-border)',
                          cursor: 'pointer',
                          backgroundColor: isExpanded ? 'rgba(217, 178, 86, 0.06)' : undefined,
                        }}
                        onClick={() => setExpandedPayrollBarber(isExpanded ? null : b.barber_id)}
                        title="Click to see daily breakdown"
                      >
                        <td
                          style={{
                            padding: 'var(--spacing-2) var(--spacing-3)',
                            fontWeight: 600,
                          }}
                        >
                          {isExpanded ? '▾' : '▸'} {b.barber_name}
                        </td>
                        <td style={{ padding: 'var(--spacing-2) var(--spacing-3)' }}>
                          {b.location_name}
                        </td>
                        <td
                          style={{
                            padding: 'var(--spacing-2) var(--spacing-3)',
                            textAlign: 'right',
                          }}
                        >
                          {b.ordinary_hours.toFixed(2)}h
                        </td>
                        <td
                          style={{
                            padding: 'var(--spacing-2) var(--spacing-3)',
                            textAlign: 'right',
                          }}
                        >
                          {b.saturday_hours.toFixed(2)}h
                        </td>
                        <td
                          style={{
                            padding: 'var(--spacing-2) var(--spacing-3)',
                            textAlign: 'right',
                            fontWeight: 600,
                          }}
                        >
                          {b.total_hours.toFixed(2)}h
                        </td>
                        <td
                          style={{
                            padding: 'var(--spacing-2) var(--spacing-3)',
                            textAlign: 'right',
                          }}
                        >
                          {currency(b.service_revenue)}
                        </td>
                        <td
                          style={{
                            padding: 'var(--spacing-2) var(--spacing-3)',
                            textAlign: 'right',
                          }}
                        >
                          {b.tips > 0 ? (
                            <span style={{ color: '#2f7d5b', fontWeight: 600 }}>
                              {currency(b.tips)}
                            </span>
                          ) : (
                            <span style={{ color: 'var(--color-text-muted)' }}>—</span>
                          )}
                        </td>
                        <td
                          style={{
                            padding: 'var(--spacing-2) var(--spacing-3)',
                            textAlign: 'right',
                          }}
                        >
                          {b.bonus_rate > 0 ? (
                            <span style={{ color: '#2f7d5b', fontWeight: 600 }}>
                              {currency(b.bonus_amount)}{' '}
                              <span
                                style={{
                                  fontSize: 'var(--font-size-xs)',
                                  fontWeight: 400,
                                  color: 'var(--color-text-muted)',
                                }}
                              >
                                ({(b.bonus_rate * 100).toFixed(0)}%)
                              </span>
                            </span>
                          ) : (
                            <span style={{ color: 'var(--color-text-muted)' }}>—</span>
                          )}
                        </td>
                      </tr>,
                      ...(isExpanded && b.daily_hours?.length > 0
                        ? [
                            <tr
                              key={`${b.barber_id}-daily`}
                              style={{ borderBottom: '1px solid var(--color-border)' }}
                            >
                              <td
                                colSpan={8}
                                style={{
                                  padding: '0 var(--spacing-3) var(--spacing-3) var(--spacing-6)',
                                }}
                              >
                                <table
                                  style={{
                                    width: 'auto',
                                    borderCollapse: 'collapse',
                                    fontSize: 'var(--font-size-xs)',
                                    marginTop: 'var(--spacing-1)',
                                  }}
                                >
                                  <thead>
                                    <tr style={{ borderBottom: '1px solid var(--color-border)' }}>
                                      <th
                                        style={{
                                          padding: '4px 12px',
                                          textAlign: 'left',
                                          fontWeight: 600,
                                          textTransform: 'uppercase',
                                          letterSpacing: '0.05em',
                                          color: 'var(--color-text-muted)',
                                        }}
                                      >
                                        Date
                                      </th>
                                      <th
                                        style={{
                                          padding: '4px 12px',
                                          textAlign: 'right',
                                          fontWeight: 600,
                                          textTransform: 'uppercase',
                                          letterSpacing: '0.05em',
                                          color: 'var(--color-text-muted)',
                                        }}
                                      >
                                        Hours
                                      </th>
                                      <th
                                        style={{
                                          padding: '4px 12px',
                                          textAlign: 'left',
                                          fontWeight: 600,
                                          textTransform: 'uppercase',
                                          letterSpacing: '0.05em',
                                          color: 'var(--color-text-muted)',
                                        }}
                                      >
                                        Type
                                      </th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {b.daily_hours.map((d: any) => (
                                      <tr key={d.date}>
                                        <td style={{ padding: '3px 12px' }}>{d.date}</td>
                                        <td
                                          style={{
                                            padding: '3px 12px',
                                            textAlign: 'right',
                                            fontWeight: 500,
                                          }}
                                        >
                                          {d.hours.toFixed(2)}h
                                        </td>
                                        <td
                                          style={{
                                            padding: '3px 12px',
                                            color: d.is_saturday
                                              ? '#D9B256'
                                              : 'var(--color-text-muted)',
                                            fontWeight: d.is_saturday ? 600 : 400,
                                          }}
                                        >
                                          {d.is_saturday ? 'Saturday' : 'Ordinary'}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </td>
                            </tr>,
                          ]
                        : []),
                    ];
                  })}
                </tbody>
              </table>

              {/* Bonus tier reference */}
              <div
                style={{
                  ...cardStyle,
                  marginTop: 'var(--spacing-4)',
                  fontSize: 'var(--font-size-xs)',
                  color: 'var(--color-text-muted)',
                }}
              >
                <strong style={{ color: 'var(--color-text)' }}>Bonus Tiers:</strong> $0–$2,499 → 0%
                &nbsp;|&nbsp; $2,500–$3,499 → 5% &nbsp;|&nbsp; $3,500–$4,499 → 10% &nbsp;|&nbsp;
                $4,500+ → 15%
              </div>
            </div>
          )}
        </section>
      )}

      {/* ── Recent Bookings ──────────────────────────────────────────── */}
      {activeTab === 'recent-bookings' && (
        <section style={{ display: 'grid', gap: 'var(--spacing-4)' }}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 'var(--spacing-3)',
            }}
          >
            <div>
              <h2 style={{ marginBottom: 'var(--spacing-1)' }}>Recent Bookings</h2>
              <p style={{ color: 'var(--color-text-muted)' }}>
                New bookings created within the selected time window. Auto-refreshes every 30s.
              </p>
            </div>
            <div style={{ display: 'flex', gap: 'var(--spacing-2)' }}>
              {RECENT_FILTERS.map((f) => (
                <button
                  key={f.id}
                  onClick={() => setRecentFilter(f.id)}
                  style={{
                    ...presetButtonStyle,
                    backgroundColor:
                      recentFilter === f.id ? 'var(--color-accent)' : 'var(--color-surface)',
                    color: recentFilter === f.id ? '#000' : 'var(--color-text)',
                    borderColor:
                      recentFilter === f.id ? 'var(--color-accent)' : 'var(--color-border)',
                  }}
                >
                  {f.label}
                </button>
              ))}
            </div>
          </div>

          {loadingRecent ? (
            <p style={{ color: 'var(--color-text-muted)' }}>Loading...</p>
          ) : !recentBookings?.length ? (
            <div
              style={{
                ...cardStyle,
                textAlign: 'center',
                padding: 'var(--spacing-8)',
                color: 'var(--color-text-muted)',
              }}
            >
              No bookings created in the{' '}
              {RECENT_FILTERS.find((f) => f.id === recentFilter)?.label.toLowerCase()}.
            </div>
          ) : (
            <>
              <div
                style={{
                  display: 'flex',
                  gap: 'var(--spacing-3)',
                  flexWrap: 'wrap',
                }}
              >
                <KpiCard label="Total Bookings" value={String(recentBookings.length)} />
                <KpiCard
                  label="Booked"
                  value={String(recentBookings.filter((b: any) => b.status === 'BOOKED').length)}
                />
                <KpiCard
                  label="Cancelled"
                  value={String(recentBookings.filter((b: any) => b.status === 'CANCELLED').length)}
                />
              </div>
              <div style={{ ...cardStyle, overflow: 'auto' }}>
                <table
                  style={{
                    width: '100%',
                    borderCollapse: 'collapse',
                    fontSize: 'var(--font-size-sm)',
                  }}
                >
                  <thead>
                    <tr
                      style={{
                        borderBottom: '1px solid var(--color-border)',
                        textAlign: 'left',
                      }}
                    >
                      <th style={{ padding: 'var(--spacing-2) var(--spacing-3)' }}>Created</th>
                      <th style={{ padding: 'var(--spacing-2) var(--spacing-3)' }}>Customer</th>
                      <th style={{ padding: 'var(--spacing-2) var(--spacing-3)' }}>Location</th>
                      <th style={{ padding: 'var(--spacing-2) var(--spacing-3)' }}>Barber</th>
                      <th style={{ padding: 'var(--spacing-2) var(--spacing-3)' }}>Appointment</th>
                      <th style={{ padding: 'var(--spacing-2) var(--spacing-3)' }}>Services</th>
                      <th style={{ padding: 'var(--spacing-2) var(--spacing-3)' }}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentBookings.map((b: any) => (
                      <tr key={b.id} style={{ borderBottom: '1px solid var(--color-border)' }}>
                        <td
                          style={{
                            padding: 'var(--spacing-2) var(--spacing-3)',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {new Date(b.created_at).toLocaleString('en-AU', {
                            timeZone: BRISBANE_TZ,
                            day: '2-digit',
                            month: 'short',
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </td>
                        <td style={{ padding: 'var(--spacing-2) var(--spacing-3)' }}>
                          <div>
                            {b.customer_name || (b.source === 'customer' ? '(Guest)' : '(Walk-in)')}
                          </div>
                          {b.customer_phone && (
                            <div
                              style={{
                                color: 'var(--color-text-muted)',
                                fontSize: 'var(--font-size-xs)',
                              }}
                            >
                              {b.customer_phone}
                            </div>
                          )}
                        </td>
                        <td style={{ padding: 'var(--spacing-2) var(--spacing-3)' }}>
                          {locationMap.get(b.location_id) || b.location_id}
                        </td>
                        <td style={{ padding: 'var(--spacing-2) var(--spacing-3)' }}>
                          {barberMap.get(b.barber_id) || b.barber_id}
                        </td>
                        <td
                          style={{
                            padding: 'var(--spacing-2) var(--spacing-3)',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {new Date(b.start_time).toLocaleString('en-AU', {
                            timeZone: BRISBANE_TZ,
                            weekday: 'short',
                            day: '2-digit',
                            month: 'short',
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </td>
                        <td style={{ padding: 'var(--spacing-2) var(--spacing-3)' }}>
                          {(b.services || []).map((s: any) => s.name).join(', ') || '—'}
                        </td>
                        <td style={{ padding: 'var(--spacing-2) var(--spacing-3)' }}>
                          <span
                            style={{
                              padding: '2px 8px',
                              borderRadius: '999px',
                              fontSize: 'var(--font-size-xs)',
                              fontWeight: 600,
                              backgroundColor:
                                b.status === 'BOOKED'
                                  ? 'rgba(59,130,246,0.15)'
                                  : b.status === 'COMPLETED'
                                    ? 'rgba(34,197,94,0.15)'
                                    : b.status === 'CANCELLED'
                                      ? 'rgba(239,68,68,0.15)'
                                      : 'rgba(156,163,175,0.15)',
                              color:
                                b.status === 'BOOKED'
                                  ? '#3b82f6'
                                  : b.status === 'COMPLETED'
                                    ? '#22c55e'
                                    : b.status === 'CANCELLED'
                                      ? '#ef4444'
                                      : '#9ca3af',
                            }}
                          >
                            {b.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </section>
      )}
    </div>
  );
}

// ─── Main export: role-based router ──────────────────────────────────────────

export function Reports() {
  const { user } = useAuth();
  if (user?.role === 'ADMIN') return <AdminReportsView />;
  return <BarberReportsView />;
}
